import { Stack, Typography } from '@mui/material';
import { AlertCard } from 'common/ui/AlertCard';
import VideoPlayer from 'common/ui/VideoPlayer';
import { Transcript } from 'components/Transcript/Transcript';
import { NoteTakerStatus } from 'generated-graphql/graphql';
import { useGetCalendarBookingQuery } from 'generated-graphql/hooks/apollo-hooks';
import React from 'react';
import { formatToDate } from 'utils/datetime';
import { getDurationFromTranscript } from 'modules/calendar/utils/transcript';
import { Loading } from 'common/ui/Loading';
import { useAmplifyFileURL } from 'core/services/amplify/storage/useAmplifyFileURL';
import { S3Bucket } from 'core/services/amplify/config/constants';

type Props = {
  calendarBookingId: string;
};

export const MeetingNotes: React.FC<Props> = ({ calendarBookingId }) => {
  const { data, loading } = useGetCalendarBookingQuery({
    variables: {
      id: calendarBookingId
    }
  });

  const { url } = useAmplifyFileURL({
    path: data?.getCalendarBooking?.notetakerMediaRecordingS3Key,
    bucket: S3Bucket.CALENDAR
  });

  if (loading) {
    return <Loading />;
  }

  if (!data?.getCalendarBooking?.noteTakerStatus) {
    return null;
  }

  const noteTakerStatus = data.getCalendarBooking.noteTakerStatus;

  if (noteTakerStatus === NoteTakerStatus.Scheduled) {
    return (
      <AlertCard title="Meeting scheduled" type="info">
        Video and transcript will appear here shortly after the meeting has
        finished.
      </AlertCard>
    );
  }

  if (noteTakerStatus === NoteTakerStatus.Processing) {
    return (
      <AlertCard title="Preparing" type="info" isLoading>
        The video and transcript will appear here shortly
      </AlertCard>
    );
  }

  const {
    getCalendarBooking: {
      startDateTime,
      calendarMeetingTemplate,
      organizerUserCalendar,
      candidateName,
      noteTakerTranscriptSummary,
      noteTakerTranscript
    }
  } = data;

  const date = formatToDate(startDateTime);

  const { duration, startTime, endTime } = getDurationFromTranscript(
    data.getCalendarBooking.noteTakerTranscript || [],
    startDateTime
  );

  return (
    <Stack spacing={2} mb={2} mt={-2} width={640}>
      <Stack>
        <Typography variant="subTitle">
          {calendarMeetingTemplate?.title}
        </Typography>
        <Typography variant="body2">
          Organised by {organizerUserCalendar?.name}. Participant:{' '}
          {candidateName}
        </Typography>
      </Stack>

      <Stack>{url && <VideoPlayer src={url.href} width="100%" />}</Stack>

      <Stack>
        <Typography whiteSpace="pre-line" variant="body1">
          {noteTakerTranscriptSummary}
        </Typography>
      </Stack>

      <Stack>
        <Typography variant="subTitle">Transcript</Typography>
        <Typography variant="body2">
          Recorded {date}, from {startTime} - {endTime} ({duration})
        </Typography>
      </Stack>

      <Transcript messages={noteTakerTranscript || []} />
    </Stack>
  );
};
